import React from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Button,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import TvIcon from '@mui/icons-material/Tv';
import PercentIcon from '@mui/icons-material/Percent';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import ReactECharts from 'echarts-for-react';

const UsageDetails = ({
    dictionary,
    periodicDict,
    billBreakdownDict,
    isFirstDayOfMonth,
    periodTrend,
    setPeriodTrend,
    setTrendData,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    fetchCustomPeriod,
    dataLoading,
    periodLabel,
    periodConsumptionTotal,
    chartOption,
    breakdownTotal,
    breakdownElectricity,
    breakdownMunicipal,
    breakdownErt,
    breakdownVat,
    navigate,
    buildingId,
    apartment,
    setEditModalOpen,
    setAddDevicesModalOpen,
}) => (
    <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Chip
                size="xlarge"
                icon={<CheckCircleOutlineIcon sx={{ color: '#42b06e', fontSize: 26 }} />}
                label={dictionary.appliancesNormal}
                sx={{
                    bgcolor: '#e8f5e9',
                    color: '#2e7d32',
                    fontWeight: 600,
                    maxWidth: 'max-content',
                    border: '1px solid',
                    borderColor: '#a5d6a7',
                    '& .MuiChip-icon': { ml: 0.5, color: '#42b06e !important' },
                }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, ml: 'auto' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
                    {periodicDict?.period ?? 'Period'}:
                </Typography>
                <Box
                    sx={{
                        display: 'inline-flex',
                        bgcolor: 'grey.200',
                        borderRadius: 2,
                        p: 0.5,
                        gap: 0,
                    }}
                >
                    {(isFirstDayOfMonth
                        ? ['last7days', 'lastMonth', 'custom']
                        : ['currentMonth', 'last7days', 'lastMonth', 'custom']
                    ).map((key) => (
                        <Button
                            key={key}
                            size="small"
                            onClick={() => { setPeriodTrend(key); if (key === 'custom') setTrendData(null); }}
                            sx={{
                                textTransform: 'none',
                                minWidth: 0,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1.5,
                                bgcolor: periodTrend === key ? '#fff' : 'transparent',
                                color: periodTrend === key ? 'primary.main' : 'grey.700',
                                boxShadow: periodTrend === key ? 1 : 'none',
                                '&:hover': {
                                    bgcolor: periodTrend === key ? '#fff' : 'rgba(255,255,255,0.5)',
                                },
                            }}
                        >
                            {key === 'currentMonth' ? dictionary.currentMonth : key === 'last7days' ? dictionary.last7Days : key === 'lastMonth' ? dictionary.lastMonth : dictionary.custom}
                        </Button>
                    ))}
                </Box>
                {periodTrend === 'custom' && (
                    <>
                        <TextField size="small" type="date" label="" value={customStart} onChange={(e) => setCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                        <TextField size="small" type="date" label="" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                        <Button size="small" variant="contained" onClick={fetchCustomPeriod} disabled={!customStart || !customEnd} sx={{ textTransform: 'none' }}>
                            {periodicDict?.retrieveData ?? 'Retrieve'}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(25, 118, 210, 0.12)',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        height: { xs: 'auto', md: '100%' },
                        minHeight: { xs: 88, md: 252 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                        {dictionary.totalConsumption} ({periodLabel})
                    </Typography>
                    <Typography
                        variant="h4"
                        color="primary.main"
                        fontWeight={700}
                    >
                        {periodConsumptionTotal.toFixed(2)} kWh
                    </Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                        {dictionary.dailyConsumptionTrend}
                    </Typography>
                    {dataLoading && periodTrend !== 'last7days' ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <ReactECharts option={chartOption} style={{ height: 220, width: '100%' }} />
                    )}
                </Paper>
            </Grid>
        </Grid>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                {dictionary.detailedCostBreakdown}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                    {dictionary.costsAreEstimated}
                </Typography>
            </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 1.5, mb: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (theme) => `${theme.palette.primary.main}14`, border: '1px solid', borderColor: 'primary.main', width: 'fit-content', flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {dictionary.totalEstimatedToPay}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">€{breakdownTotal.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', minWidth: 140, flex: '1 1 140px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: '#FF9800', width: 36, height: 36 }}><BoltIcon sx={{ color: 'white', fontSize: 18 }} /></Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{billBreakdownDict?.electricityCost ?? 'Electricity'}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, ml: 0.5 }}>€{breakdownElectricity.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', minWidth: 140, flex: '1 1 140px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: '#4CAF50', width: 36, height: 36 }}><BusinessIcon sx={{ color: 'white', fontSize: 18 }} /></Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{billBreakdownDict?.municipalTaxes ?? 'Municipal'}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, ml: 0.5 }}>€{breakdownMunicipal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', minWidth: 140, flex: '1 1 140px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: '#F44336', width: 36, height: 36 }}><TvIcon sx={{ color: 'white', fontSize: 18 }} /></Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{billBreakdownDict?.ert ?? 'ERT'}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, ml: 0.5 }}>€{breakdownErt.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: 'rgba(33, 150, 243, 0.1)', minWidth: 140, flex: '1 1 140px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: '#2196F3', width: 36, height: 36 }}><PercentIcon sx={{ color: 'white', fontSize: 18 }} /></Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{billBreakdownDict?.vat ?? 'VAT'}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, ml: 0.5 }}>€{breakdownVat.toFixed(2)}</Typography>
                </Box>
            </Box>
        </Box>
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => setEditModalOpen(true)}
                >
                    {dictionary.editHouseButton || 'Edit'}
                </Button>
                <Button
                    size="small"
                    startIcon={<DevicesOutlinedIcon />}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => setAddDevicesModalOpen(true)}
                >
                    {dictionary.addDevicesButton || 'Add Devices'}
                </Button>
                <Button size="small" startIcon={<InfoOutlinedIcon />} endIcon={<ChevronRightIcon />} sx={{ textTransform: 'none', color: 'primary.main' }} onClick={() => navigate(`/building/${buildingId}/apartment/${apartment.id}`)}>
                    {dictionary.moreAdvancedInsights}
                </Button>
            </Box>
        </Box>
    </>
);

export default UsageDetails;
